import * as React from "react";
import { IcEye, IcEyeOff } from "@racoon/icons";
import { Button, cn } from "@racoon/ui";
import { MAP_CONTROL_CLASS } from "~/shared/ui/map-control";
import { Namespace, useT } from "@racoon/i18n";

type Props = {
  collapsed: boolean;
  onToggle: () => void;
  /**
   * Width classes of the expanded panel, e.g. "w-[400px] 2xl:w-[540px]". Also
   * applied to the inner map box, so the collapsed strip shows a clipped edge of
   * the very same map instead of a re-fitted 48px-wide one.
   */
  expandedWidthClassName: string;
  className?: string;
  /** Border classes. A border on `className` would narrow the box but not the
   *  map inside it, clipping the map's right edge and its controls' 8px gutter. */
  frameClassName?: string;
  children: React.ReactNode;
};

const COLLAPSED_WIDTH_CLASS = "w-12";

/** Timing of the collapse/expand. Hosts that animate the space they give back
 *  (e.g. the route sheet's cascade margin) use the same value so both move together. */
export const MAP_PANEL_TRANSITION_TIMING = "duration-[400ms] ease-in-out";

/** The toggle owns the first slot of the map's top-right corner, so the map's own
 *  control stack starts one slot lower. Hosts pass this to `MapWithGeofence`. */
export const MAP_PANEL_CONTROL_OFFSET = "top-12 right-2";

/**
 * Side panel that animates between its expanded width and a narrow strip. The
 * map inside keeps its expanded size the whole time (only the outer box
 * shrinks), so Leaflet never needs `invalidateSize`, keeps its zoom/center and
 * is never remounted — expanding again does not refire the map's RPCs.
 */
export const CollapsibleMapPanel: React.FC<Props> = ({
  collapsed,
  onToggle,
  expandedWidthClassName,
  className,
  frameClassName,
  children,
}) => {
  const t = useT();
  const label = collapsed ? t(Namespace.Common, "expandMap") : t(Namespace.Common, "collapseMap");

  return (
    <div
      className={cn(
        // No border here: chrome is the host's, so wrapping a map does not restyle it.
        "relative isolate z-0 shrink-0 overflow-hidden rounded-xl transition-[width]",
        MAP_PANEL_TRANSITION_TIMING,
        collapsed ? COLLAPSED_WIDTH_CLASS : expandedWidthClassName,
        className,
      )}
    >
      {/* Left-anchored at the expanded width: the strip reveals the map's left
          edge. Inert while collapsed so a wheel over the strip does not zoom it. */}
      <div
        aria-hidden={collapsed}
        className={cn(
          "absolute inset-y-0 left-0",
          expandedWidthClassName,
          collapsed && "pointer-events-none",
        )}
      >
        {children}
      </div>

      {frameClassName && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 z-10 rounded-[inherit]",
            frameClassName,
          )}
        />
      )}

      {/* First slot of the map's control stack: same 32px geometry, 8px from the
          edge, with the map's own controls continuing below it. */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggle}
        title={label}
        aria-label={label}
        aria-expanded={!collapsed}
        className={cn(
          "absolute right-2 top-2 z-10 max-h-8 max-w-8 hover:bg-slate-100",
          MAP_CONTROL_CLASS,
        )}
      >
        {/* Figma fills: #151A24 (no token) expanded, Text/error collapsed. */}
        {collapsed ? (
          <IcEyeOff className="h-5 w-5 text-text-error" />
        ) : (
          <IcEye className="h-5 w-5 text-[#151A24]" />
        )}
      </Button>
    </div>
  );
};
